import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';

// hooks
import { useRouter } from 'src/routes/hooks';
import { useAuthContext } from 'src/auth/hooks';
import { useIsAmcRequired } from 'src/hooks/use-is-amc-required';

// routes

import { CONFIG } from 'src/config-global';

// utils
import axiosInstance from 'src/utils/axios';
import { STORAGE_KEY } from 'src/auth/context/jwt/constant';
import { paths } from 'src/routes/paths';

// components
import { toast } from 'src/components/snackbar';
import { Label } from 'src/components/label';
import NewBadge from 'src/components/badge/badge';
import { Iconify } from 'src/components/iconify';
import { SvgColor } from 'src/components/svg-color';

// ----------------------------------------------------------------------

const supportAuthApi = import.meta.env.VITE_SUPPORT_AUTH_API;

const icon = (name) => <SvgColor src={`${CONFIG.site.basePath}/assets/icons/navbar/${name}.svg`} />;

const ICONS = {
  job: icon('ic-job'),
  blog: icon('ic-blog'),
  chat: icon('ic-chat'),
  mail: icon('ic-mail'),
  user: icon('ic-user'),
  file: icon('ic-file'),
  lock: icon('ic-lock'),
  tour: icon('ic-tour'),
  order: icon('ic-order'),
  label: icon('ic-label'),
  blank: icon('ic-blank'),
  kanban: icon('ic-kanban'),
  folder: icon('ic-folder'),
  course: icon('ic-course'),
  banking: icon('ic-banking'),
  booking: icon('ic-booking'),
  invoice: icon('ic-invoice'),
  product: icon('ic-product'),
  calendar: icon('ic-calendar'),
  disabled: icon('ic-disabled'),
  external: icon('ic-external'),
  menuItem: icon('ic-menu-item'),
  ecommerce: icon('ic-ecommerce'),
  analytics: icon('ic-analytics'),
  dashboard: icon('ic-dashboard'),
  new_dashboard: icon('ic-new_dashboard'),
  parameter: icon('ic-parameter'),
  managerAsset: icon('ic-asset'),
  subscription: icon('ic-subscription'),
  sso: icon('ic-sso'),
  others: icon('ic-others'),
  amc: icon('ic-amc'),
  expiry: icon('ic-expiry'),
  periodic_check: icon('ic-periodic-check'),
  audit: icon('ic-audit'),
  incident: icon('ic-incident'),
  serivce: icon('ic-service'),
  network: icon('ic-network'),
  branches: icon('ic-branches'),
  devices: icon('ic-devices'),
  topology: icon('ic-topology'),
  alerts: icon('ic-alerts'),
  assets: icon('ic-assets'),
  workorder: icon('ic-workorders'),
  preventive_maintenance: icon('ic-preventive-maintenance'),
  inventory: icon('ic-inventory'),
  discovery: icon('ic-smart-discovery'),
  start: icon('ic-start-verification'),
};

// ----------------------------------------------------------------------

const supportPortalUrl = import.meta.env.VITE_SUPPORT_PORTAL_URL;

export const NavData = () => {
  const {
    user,
    checkUserSession,
    isRDPRequired,
    isSSOLoginRequired,
    isDiscoveryRequired,
    isAiImageDiscoveryRequired,
    isCmmsRequired,
    isSystemMonitoringRequired,
  } = useAuthContext();

  const isAmcRequired = useIsAmcRequired();

  // local Storage
  const authAppAccessToken = localStorage.getItem('authAppAccessToken');

  const [menu, setMenu] = useState([]);

  // 🔹 Helper function to recursively replace icon strings
  const mapIcons = (menuItems) =>
    menuItems.map((section) => ({
      ...section,
      items: section.items.map((item) => ({
        ...item,
        icon: ICONS[item.icon] || ICONS.blank, // Replace string with actual icon component
        children: item.children
          ? item.children.map((child) => ({
              ...child,
              icon: child.icon ? ICONS[child.icon] || ICONS.blank : undefined,
            }))
          : undefined,
      })),
    }));

  useEffect(() => {
    if (user?.company?.navMenu) {
      const processedMenu = mapIcons(user.company.navMenu);
      setMenu(processedMenu);
    }
  }, [user]);

  const handleCreateToken = useCallback(async () => {
    try {
      const reqToken = await axios.post(
        `${supportAuthApi}/auth/requestAppToken`,
        { appId: 'OnTaskSupport' },
        {
          headers: { Authorization: `Bearer ${authAppAccessToken}` },
        }
      );
      const { appId, appToken } = reqToken.data;
      window.open(
        `${supportPortalUrl}/auth/jwt/sign-in?returnTo=%2Ftickets&token=${appToken}`,
        '_blank',
        'noopener'
      );
    } catch (error) {
      toast.error(error.message);
    }
  }, [authAppAccessToken]);

  const handleNewClick = (role) => {
    axiosInstance
      .get(`user/update-user/ticket-created?role=${role}`)
      .then((res) => {
        checkUserSession();
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // console.log('api call', );

  // Return company nav menu if it exists
  if (user?.company?.navMenu) {
    return menu;
  }

  const dynamicNav = user?.categoryMenu?.map((category) => {
    const formattedItem = category?.name?.replace?.(/\s+/g, '-');
    const basePath = `/asset-management/dynamic-assets/${formattedItem}`;
    return {
      title: category?.name,
      path: basePath,
      icon: <Iconify icon={category?.icon ?? 'solar:transfer-horizontal-bold-duotone'} />,
      userTypes: [
        'admin',
        ...(user?.role?.find((item) => item?.name === 'assetadmin')?.category?.length === 0 ||
        user?.role?.some(
          (items) => items?.name === 'assetadmin' && items?.category?.includes(category?.name)
        )
          ? ['assetadmin']
          : []),
        ...(user?.role?.find((item) => item?.name === 'technician')?.category?.length === 0 ||
        user?.role?.some(
          (items) => items?.name === 'technician' && items?.category?.includes(category?.name)
        )
          ? ['technician']
          : []),
      ],
      children: [
        {
          title: `${category?.name} Assets`,
          path: basePath,
          userTypes: ['admin', 'assetadmin', 'technician'],
        },
        {
          title: `${category?.name} Items`,
          path: `${basePath}/items`,
          userTypes: ['admin', 'assetadmin', 'technician'],
        },
        {
          title: `Alerts`,
          path: `${basePath}/alerts`,
          userTypes: ['admin', 'assetadmin', 'technician'],
          info: (
            <Label color="warning">
              {user?.alertCounts?.[category?.name] > 99
                ? ' 99+'
                : user?.alertCounts?.[category?.name]}
            </Label>
          ),
        },
        // {
        //   title: `QR List`,
        //   path: `${basePath}/pdf`,
        //   userTypes: ['admin', 'assetadmin', 'technician'],
        // },
      ],
    };
  });

  return [
    /**
     * Overview
     */
    isSSOLoginRequired && {
      title: 'SSO LOGIN',
      subheader: 'SSO LOGIN',
      path: '/ms-azure-sign-in',
      icon: ICONS.sso,
      userTypes: ['admin'],
    },

    {
      subheader: 'OVERVIEW',
      userTypes: ['admin', 'assetadmin', 'technician', 'itsupport', 'employee'],
      isOpen: true,
      items: [
        {
          title: 'Admin Dashboard',
          path: '/dashboard',
          icon: ICONS.network,
          userTypes: ['admin', 'assetadmin'],
        },
        {
          title: 'IT Support Dashboard',
          path: '/it-support/dashboard',
          icon: ICONS.network,
          userTypes: ['admin', 'itsupport'],
        },
        {
          title: 'Technician Dashboard',
          path: '/technician/dashboard',
          icon: ICONS.network,
          userTypes: ['admin', 'technician'],
        },
        {
          title: 'User Dashboard',
          path:
            user?.role?.length === 1 && user?.role[0]?.name === 'employee'
              ? '/user'
              : '/user/dashboard/admin',
          icon: ICONS.network,
          userTypes: ['admin', 'employee'],
        },
        isCmmsRequired && {
          title: 'CMMS Dashboard',
          path: '/cmms-dashboard',
          icon: ICONS.network,
          userTypes: ['admin', 'assetadmin'],
        },
      ].filter(Boolean),
    },

    isSystemMonitoringRequired && {
      subheader: 'System Monitoring',
      userTypes: ['admin', 'assetadmin'],
      items: [
        {
          title: 'Device Monitoring',
          path: '/',
          icon: ICONS.devices,
          userTypes: ['admin', 'assetadmin'],
          onClick: () => {
            const jwtToken = localStorage.getItem(STORAGE_KEY);
            const urlParams = new URLSearchParams();
            if (jwtToken) {
              urlParams.append('token', jwtToken);
            }
            const netScanUrl = `${import.meta.env.VITE_NET_SCAN_APP}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
            window.open(netScanUrl, '_blank');
          },
        },
        // {
        //   title: 'Application Monitoring',
        //   path: '/',
        //   icon: ICONS.branches,
        //   userTypes: ['admin', 'assetadmin'],
        // },
      ],
    },

    ...(isDiscoveryRequired || isAiImageDiscoveryRequired
      ? [
          {
            subheader: 'ASSET DISCOVERY',
            isOpen: true,
            userTypes: ['admin', 'assetadmin'],
            items: [
              ...(isDiscoveryRequired
                ? [
                    {
                      title: 'Network Discovery',
                      path: paths.app.NetworkDiscovery.root,
                      icon: <Iconify icon="carbon:ibm-watson-discovery" />,

                      userTypes: ['admin', 'assetadmin'],
                    },
                  ]
                : []),
              ...(isAiImageDiscoveryRequired
                ? [
                    {
                      title: 'Smart Discovery',
                      path: paths.app.DiscoverByImg.root,
                      icon: ICONS.discovery,
                      userTypes: ['admin', 'assetadmin'],
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),

    {
      subheader: 'CONSOLE',
      userTypes: ['manager', 'technician', 'itsupport'],
      items: [
        // {
        //   title: 'My Assets',
        //   path: paths.app.assets.root,
        //   icon: <Iconify icon="solar:bill-list-bold" />,
        //   userTypes: ['admin', 'assetadmin', 'technician', 'manager', 'itsupport', 'employee'],
        // },
        // {
        //   title: 'My Tickets',
        //   path: paths.app.userTickets.root,
        //   icon: <Iconify icon="solar:bill-list-bold" />,
        //   userTypes: ['assetadmin', 'employee', 'technician', 'admin', 'manager', 'itsupport'],
        // },
        {
          title: 'Users',
          path: paths.app.users.root,
          icon: <Iconify icon="solar:bill-list-bold" />,
          userTypes: ['manager', 'technician', 'itsupport'],
        },
      ],
    },

    // ---------------------------------------------------------------------
    {
      subheader: 'ASSET MANAGEMENT',
      userTypes: [
        'admin',
        ...(user?.role?.some((item) => item?.name === 'assetadmin') ? ['assetadmin'] : []),
        ...(user?.role?.some((item) => item?.name === 'technician') ? ['technician'] : []),
      ],
      items: [
        {
          title: 'Hardware',
          path: paths.app.asset_management.hardware_assets,
          icon: <Iconify icon="nrk:hardware-computer" />,
          userTypes: [
            'admin',
            ...(user?.role?.find((item) => item?.name === 'assetadmin')?.category?.length === 0 ||
            user?.role?.some(
              (item) => item?.name === 'assetadmin' && item?.category?.includes('Hardware')
            )
              ? ['assetadmin']
              : []),
            ...(user?.role?.find((item) => item?.name === 'technician')?.category?.length === 0 ||
            user?.role?.some(
              (items) => items?.name === 'technician' && items?.category?.includes('Hardware')
            )
              ? ['technician']
              : []),
          ],
          children: [
            {
              title: 'Hardware Assets',
              path: paths.app.asset_management.hardware_assets,
              userTypes: ['admin', 'assetadmin', 'technician'],
            },
            {
              title: 'Hardware Items',
              path: paths.app.asset_management.hardware_assets_items,
              userTypes: ['admin', 'assetadmin', 'technician'],
            },
            {
              title: 'Alerts',
              path: paths.app.asset_management.hardware_assets_alerts,
              userTypes: ['admin', 'assetadmin', 'technician'],
              info: (
                <Label color="warning">
                  {user?.alertCounts?.Hardware > 99 ? ' 99+' : user?.alertCounts?.Hardware}
                </Label>
              ),
            },
            // {
            //   title: 'QR List',
            //   path: paths.app.asset_management.hardware_assets_pdf,
            //   userTypes: ['admin', 'assetadmin', 'technician'],
            // },
          ],
        },
        {
          title: 'Software',
          path: paths.app.asset_management.software_assets,
          icon: <Iconify icon="mingcute:windows-fill" />,
          userTypes: [
            'admin',
            ...(user?.role?.find((item) => item?.name === 'assetadmin')?.category?.length === 0 ||
            user?.role?.some(
              (item) => item?.name === 'assetadmin' && item?.category?.includes('Software')
            )
              ? ['assetadmin']
              : []),
            ...(user?.role?.find((item) => item?.name === 'technician')?.category?.length === 0 ||
            user?.role?.some(
              (items) => items?.name === 'technician' && items?.category?.includes('Software')
            )
              ? ['technician']
              : []),
          ],
          children: [
            {
              title: 'Software Assets',
              path: paths.app.asset_management.software_assets,
              userTypes: ['admin', 'assetadmin', 'technician'],
            },
            {
              title: 'Alerts',
              path: paths.app.asset_management.software_assets_alerts,
              userTypes: ['admin', 'assetadmin', 'technician'],
            },
            {
              title: 'Timeline',
              path: paths.app.asset_management.software_timelines,
              userTypes: ['admin', 'assetadmin', 'technician'],
            },
          ],
        },
        {
          title: 'Others',
          path: paths.app.asset_management.other_assets,
          icon: <Iconify icon="streamline-block:other-ui-layers" />,
          userTypes: [
            'admin',
            ...(user?.role?.find((item) => item?.name === 'assetadmin')?.category?.length === 0 ||
            user?.role?.some(
              (item) => item?.name === 'assetadmin' && item?.category?.includes('Others')
            )
              ? ['assetadmin']
              : []),
            ...(user?.role?.find((item) => item?.name === 'technician')?.category?.length === 0 ||
            user?.role?.some(
              (items) => items?.name === 'technician' && items?.category?.includes('Others')
            )
              ? ['technician']
              : []),
          ],
          children: [
            {
              title: 'Other Assets',
              path: paths.app.asset_management.other_assets,
              userTypes: ['admin', 'assetadmin', 'technician'],
            },
            {
              title: 'Other Items',
              path: paths.app.asset_management.other_assets_items,
              userTypes: ['admin', 'assetadmin', 'technician'],
            },
            {
              title: 'Alerts',
              path: paths.app.asset_management.other_assets_alerts,
              userTypes: ['admin', 'assetadmin', 'technician'],
              info: (
                <Label color="warning">
                  {user?.alertCounts?.Others > 99 ? ' 99+' : user?.alertCounts?.Others}
                </Label>
              ),
            },
            // {
            //   title: 'QR List',
            //   path: paths.app.asset_management.other_assets_pdf,
            //   userTypes: ['admin', 'assetadmin', 'technician'],
            // },
          ],
        },
        ...(dynamicNav || []),
        ...(isAmcRequired
          ? [
              {
                title: 'AMC Management',
                userTypes: ['admin', 'assetadmin'],
                path: '/amc-contracts',
                icon: ICONS.amc,
                children: [
                  isAmcRequired && {
                    title: 'AMC Contracts',
                    path: '/amc-contracts',
                    icon: ICONS.user,
                    userTypes: ['admin', 'assetadmin'],
                  },
                  isAmcRequired && {
                    title: 'AMC Alerts',
                    icon: ICONS.user,
                    path: paths.app.alerts.root,
                    userTypes: ['admin', 'assetadmin'],
                  },
                ].filter(Boolean),
              },
            ]
          : []),
        {
          icon: ICONS.expiry,
          title: 'Expiry & Renewal Alerts',
          path: paths.app.alerts.warranty_subscription,
          userTypes: ['admin', 'assetadmin'],
        },
        ...(isRDPRequired
          ? [
              {
                title: 'Remote Desktop',
                path: paths.app.remoteAssets.root,
                icon: <Iconify icon="mdi:remote-desktop" />,
                userTypes: ['admin', 'assetadmin', 'technician'],
              },
            ]
          : []),
        {
          title: 'Asset periodic Check',
          path: paths.app.periodicCheck.root,
          icon: ICONS.periodic_check,
          userTypes: ['admin', 'assetadmin'],
        },
        {
          title: 'Asset Audit',
          path: paths.app.asset_audit.root,
          icon: ICONS.audit,
          userTypes: ['admin', 'assetadmin'],
        },
      ]?.filter(Boolean),
    },

    {
      subheader: 'Service MANAGEMENT ',
      userTypes: ['admin', 'assetadmin', 'technician', 'manager', 'itsupport'],

      items: [
        {
          title: 'Incident Management',
          path: '/incident-ticket',
          icon: <Iconify icon="f7:exclamationmark-octagon-fill" />,
          userTypes: ['admin', 'assetadmin', 'technician'],
          children: [
            // {
            //   title: 'Active Incidents (Manager)',
            //   path: '/ticket/my-ticket/all-tickets',
            //   userTypes: ['manager'],
            // },
            {
              title: 'Active Incidents (Technician)',
              path: paths.app.tickets.technician_incident_list,
              userTypes: ['technician'],
              info: (
                <>
                  <NewBadge code={user?.isNewTicketCreated} />
                  <Label color="success">
                    {user?.allStageIssueCount > 99 ? ' 99+' : user?.allStageIssueCount}
                  </Label>{' '}
                </>
              ),
              onClick: () => {
                if (user?.isNewTicketCreated) {
                  handleNewClick('technician'); // Call the API
                }
              },
            },
            {
              title: 'Active Incidents',
              path: paths.app.tickets.incident_ticket_list,
              userTypes: ['admin', 'assetadmin'],
              info: (
                <Label color="success">
                  {user?.allStageIssueCount > 99 ? ' 99+' : user?.allStageIssueCount}
                </Label>
              ),
            },
            {
              title: 'Closed Incidents',
              path: paths.app.tickets.incident_closed_list,
              userTypes: ['admin', 'assetadmin', 'technician'],
              info: (
                <Label color="error">
                  {user?.allClosedIssueCount > 99 ? ' 99+' : user?.allClosedIssueCount}
                </Label>
              ),
            },
            {
              title: 'Modification Requests',
              path: paths.app.tickets.incident_modification_list,
              userTypes: ['admin', 'assetadmin', 'technician'],
            },
          ],
        },
        {
          title: 'Service Management',
          path: '/ticket',
          icon: ICONS.serivce,
          userTypes: ['admin', 'assetadmin', 'technician', 'manager', 'itsupport'],
          children: [
            {
              title: 'Manager Approvals',
              path: paths.app.tickets.manager_approvalList,
              userTypes: ['admin', 'manager'],
              info: <NewBadge code={user?.isNewManagerTicketCreated} />,
              onClick: () => {
                if (user?.isNewGeneralTicketCreated || user?.isNewManagerTicketCreated) {
                  handleNewClick('manager'); // Call the API
                }
              },
            },
            {
              title: 'Pending Services',
              path: paths.app.tickets.approval_list,
              userTypes: ['admin', 'assetadmin'],
              info: (
                <>
                  <NewBadge code={user?.isNewTicketCreated} />
                  <Label color="warning">{user?.allCount > 99 ? ' 99+' : user?.allCount}</Label>
                </>
              ),
              onClick: () => {
                if (user?.allCountWithoutIssue) {
                  handleNewClick('assetadmin'); // Call the API
                }
              },
              // info: ,
            },
            {
              title: 'Active Services (Manager)',
              path: '/ticket/my-ticket/all-tickets',
              userTypes: ['manager'],
            },
            {
              title: 'Active Services',
              path: paths.app.tickets.ticket_list,
              userTypes: ['admin', 'assetadmin'],
              info: (
                <Label color="success">
                  {user?.allStageWithoutIssue > 99 ? ' 99+' : user?.allStageWithoutIssue}
                </Label>
              ),
            },
            {
              title: 'General Tickets',
              path: paths.app.tickets.it_support_list,
              userTypes: ['itsupport'],
              info: <NewBadge code={user?.isNewGeneralTicketCreated} />,
              onClick: () => {
                // if (user?.isNewGeneralTicketCreated || user?.isNewManagerTicketCreated) {
                handleNewClick('itsupport'); // Call the API
                // }
              },
            },

            {
              title: 'Active Services (Technician)',
              path: paths.app.tickets.technician_approval_list,
              userTypes: ['technician'],
              info: (
                <>
                  <NewBadge code={user?.isNewTicketCreated} />
                  <Label color="success">
                    {user?.allStageWithoutIssue > 99 ? ' 99+' : user?.allStageWithoutIssue}
                  </Label>{' '}
                </>
              ),
              onClick: () => {
                if (user?.isNewTicketCreated) {
                  handleNewClick('technician'); // Call the API
                }
              },
            },

            {
              title: 'Closed Services',
              path: paths.app.tickets.closed_ticket_list,
              userTypes: ['admin', 'assetadmin', 'technician'],
              info: (
                <Label color="error">
                  {user?.allClosedWithoutIssue > 99 ? ' 99+' : user?.allClosedWithoutIssue}
                </Label>
              ),
            },
            {
              title: 'Modification Requests',
              path: paths.app.tickets.modification,
              userTypes: ['admin', 'assetadmin', 'technician'],
            },
          ],
        },
        {
          title: 'General Request',
          path: '/it-support',
          icon: <Iconify icon="f7:exclamationmark-octagon-fill" />,

          userTypes: ['admin'],
        },
        ...(user?.isOnTaskAdded
          ? [
              {
                title: 'External Tickets',
                path: '#',
                onClick: () => {
                  handleCreateToken();
                },
                icon: ICONS.label,
                info: <Iconify width={18} icon="prime:external-link" />,
                userTypes: ['admin', 'itsupport'],
              },
            ]
          : []),
      ],
    },

    ...(isCmmsRequired
      ? [
          {
            subheader: 'CMMS',
            userTypes: ['admin', 'assetadmin'],
            items: [
              {
                title: 'Assets',
                path: paths.app.cmms_assets.root,
                icon: ICONS.assets,
                userTypes: ['admin', 'assetadmin'],
              },
              {
                title: 'Work Orders',
                path: paths.app.workOrder.root,
                icon: ICONS.workorder,
                userTypes: ['admin', 'assetadmin'],
              },
              {
                title: 'Preventive Maintenance',
                path: paths.app.preventiveMaintenance.root,
                icon: ICONS.preventive_maintenance,
                userTypes: ['admin', 'assetadmin'],
              },
              {
                title: 'Maintenance Requests',
                path: paths.app.maintenanceRequests.root,
                icon: <Iconify icon="fluent:mail-settings-20-filled" />,
                userTypes: ['admin', 'assetadmin'],
              },
              {
                title: 'Inventory',
                path: paths.app.inventory.root,
                icon: ICONS.inventory,
                userTypes: ['admin', 'assetadmin'],
              },
            ],
          },
        ]
      : []),

    {
      subheader: 'Analytics & Insights',
      userTypes: ['admin', 'assetadmin'],

      items: [
        {
          title: ' Reports',
          path: paths.app.reports.root,
          icon: ICONS.analytics,
          userTypes: ['admin', 'assetadmin'],
        },

        {
          title: 'Spend Insights',
          path: paths.app.assetSpendManagement.root,
          icon: <Iconify icon="solar:hand-money-bold-duotone" />,
          userTypes: ['admin', 'assetadmin'],
          children: [
            {
              title: ' Asset Spend',
              path: paths.app.assetSpendManagement.assetSpendOverview,
              userTypes: ['admin', 'assetadmin'],
            },
            {
              title: 'User Spend',
              path: paths.app.assetSpendManagement.userSpendList,
              userTypes: ['admin', 'assetadmin'],
            },
          ],
        },
      ],
    },

    {
      subheader: 'MASTER CONFIGURATIONS',
      userTypes: ['admin', 'assetadmin'],
      items: [
        {
          title: 'Users',
          path: paths.app.settings.users,
          userTypes: ['admin', 'assetadmin'],
          icon: ICONS.label,
        },
        {
          title: 'Asset Configurations',
          path: paths.app.settings.asset_config,
          userTypes: ['admin', 'assetadmin'],
          icon: ICONS.label,
          children: [
            {
              title: 'IT Assets',
              path: '/asset-config/it-assets',
              userTypes: ['admin', 'assetadmin'],
            },
            ...(isCmmsRequired
              ? [
                  {
                    title: 'CMMS',
                    path: '/asset-config/cmms',
                    userTypes: ['admin', 'assetadmin'],
                  },
                ]
              : []),
          ],
        },
        {
          title: 'Asset Checklist',
          path: paths.app.settings.asset_checklist,
          userTypes: ['admin', 'assetadmin'],
          icon: ICONS.label,
        },
        {
          title: 'Ticket Configurations',
          path: paths.app.settings.ticket,
          userTypes: ['admin', 'assetadmin'],
          icon: ICONS.label,
        },
        {
          title: 'Tag Configurations',
          path: paths.app.settings.tag_list,
          userTypes: ['admin', 'assetadmin'],
          icon: ICONS.label,
        },
        {
          title: 'Vendors & Resellers',
          path: '/settings/vendor-reseller',
          userTypes: ['admin', 'assetadmin'],
          icon: ICONS.label,
        },
        {
          title: 'QR Generator',
          path: '/asset/pdf',
          userTypes: ['admin', 'assetadmin', 'technician'],
          icon: ICONS.label,
        },
        {
          title: 'Locations',
          path: paths.app.settings.location,
          userTypes: ['admin', 'assetadmin'],
          icon: ICONS.label,
        },
        {
          title: 'Currency Manager',
          path: paths.app.settings.currency_manager,
          userTypes: ['admin', 'assetadmin'],
          icon: ICONS.label,
        },
      ],
    },
    // ---------------------------------------------------------------------

    // ...(isDiscoveryRequired || isAiImageDiscoveryRequired
    //   ? [
    //       {
    //         subheader: 'Device discovery',
    //         userTypes: ['admin', 'assetadmin'],
    //         items: [
    //           ...(isDiscoveryRequired
    //             ? [
    //                 {
    //                   title: 'Network discovery',
    //                   path: paths.app.NetworkDiscovery.root,
    //                   icon: <Iconify icon="heroicons:magnifying-glass-20-solid" />,
    //                   userTypes: ['admin', 'assetadmin'],
    //                 },
    //               ]
    //             : []),
    //           ...(isAiImageDiscoveryRequired
    //             ? [
    //                 {
    //                   title: 'Discover By Image',
    //                   path: paths.app.DiscoverByImg.root,
    //                   icon: <Iconify icon="bi:images" />,
    //                   userTypes: ['admin', 'assetadmin'],
    //                 },
    //               ]
    //             : []),
    //         ],
    //       },
    //     ]
    //   : []),
    // {
    //   subheader: 'CONSOLE',
    //   userTypes: ['manager', 'technician', 'itsupport'],
    //   items: [
    //     // {
    //     //   title: 'My Assets',
    //     //   path: paths.app.assets.root,
    //     //   icon: <Iconify icon="solar:bill-list-bold" />,
    //     //   userTypes: ['admin', 'assetadmin', 'technician', 'manager', 'itsupport', 'employee'],
    //     // },
    //     // {
    //     //   title: 'My Tickets',
    //     //   path: paths.app.userTickets.root,
    //     //   icon: <Iconify icon="solar:bill-list-bold" />,
    //     //   userTypes: ['assetadmin', 'employee', 'technician', 'admin', 'manager', 'itsupport'],
    //     // },
    //     {
    //       title: 'Users',
    //       path: paths.app.users.root,
    //       icon: <Iconify icon="solar:bill-list-bold" />,
    //       userTypes: ['manager', 'technician', 'itsupport'],
    //     },
    //   ],
    // },
    // // Asset Management
    // {
    //   subheader: 'ASSET MANAGEMENT',
    //   userTypes: [
    //     'admin',
    //     ...(user?.role?.some((item) => item?.name === 'assetadmin') ? ['assetadmin'] : []),
    //     ...(user?.role?.some((item) => item?.name === 'technician') ? ['technician'] : []),
    //   ],
    //   items: [
    //     isRDPRequired && {
    //       title: 'Remote Desktop',
    //       path: paths.app.remoteAssets.root,
    //       icon: <Iconify icon="mdi:remote-desktop" />,
    //       userTypes: ['admin', 'assetadmin', 'technician'],
    //     },
    //     {
    //       title: 'Periodic Check',
    //       path: paths.app.periodicCheck.root,
    //       icon: <Iconify icon="lets-icons:check-fill" />,
    //       userTypes: ['admin', 'assetadmin'],
    //     },

    //     {
    //       title: 'Hardware',
    //       path: paths.app.asset_management.hardware_assets,
    //       icon: ICONS.managerAsset,
    //       userTypes: [
    //         'admin',
    //         ...(user?.role?.find((item) => item?.name === 'assetadmin')?.category?.length === 0 ||
    //         user?.role?.some(
    //           (item) => item?.name === 'assetadmin' && item?.category?.includes('Hardware')
    //         )
    //           ? ['assetadmin']
    //           : []),
    //         ...(user?.role?.find((item) => item?.name === 'technician')?.category?.length === 0 ||
    //         user?.role?.some(
    //           (items) => items?.name === 'technician' && items?.category?.includes('Hardware')
    //         )
    //           ? ['technician']
    //           : []),
    //       ],
    //       children: [
    //         {
    //           title: 'Hardware Assets',
    //           path: paths.app.asset_management.hardware_assets,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //         },
    //         {
    //           title: 'Hardware Items',
    //           path: paths.app.asset_management.hardware_assets_items,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //         },
    //         {
    //           title: 'Alerts',
    //           path: paths.app.asset_management.hardware_assets_alerts,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //           info: (
    //             <Label color="warning">
    //               {user?.alertCounts?.Hardware > 99 ? ' 99+' : user?.alertCounts?.Hardware}
    //             </Label>
    //           ),
    //         },
    //         {
    //           title: 'QR List',
    //           path: paths.app.asset_management.hardware_assets_pdf,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //         },
    //       ],
    //     },
    //     {
    //       title: 'Software',
    //       path: paths.app.asset_management.software_assets,
    //       icon: <Iconify icon="mingcute:windows-fill" />,
    //       userTypes: [
    //         'admin',
    //         ...(user?.role?.find((item) => item?.name === 'assetadmin')?.category?.length === 0 ||
    //         user?.role?.some(
    //           (item) => item?.name === 'assetadmin' && item?.category?.includes('Software')
    //         )
    //           ? ['assetadmin']
    //           : []),
    //         ...(user?.role?.find((item) => item?.name === 'technician')?.category?.length === 0 ||
    //         user?.role?.some(
    //           (items) => items?.name === 'technician' && items?.category?.includes('Software')
    //         )
    //           ? ['technician']
    //           : []),
    //       ],
    //       children: [
    //         {
    //           title: 'Software Assets',
    //           path: paths.app.asset_management.software_assets,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //         },
    //         {
    //           title: 'Alerts',
    //           path: paths.app.asset_management.software_assets_alerts,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //         },
    //         {
    //           title: 'Timeline',
    //           path: paths.app.asset_management.software_timelines,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //         },
    //       ],
    //     },
    //     {
    //       title: 'Others',
    //       path: paths.app.asset_management.other_assets,
    //       icon: <Iconify icon="solar:transfer-horizontal-bold-duotone" />,
    //       userTypes: [
    //         'admin',
    //         ...(user?.role?.find((item) => item?.name === 'assetadmin')?.category?.length === 0 ||
    //         user?.role?.some(
    //           (item) => item?.name === 'assetadmin' && item?.category?.includes('Others')
    //         )
    //           ? ['assetadmin']
    //           : []),
    //         ...(user?.role?.find((item) => item?.name === 'technician')?.category?.length === 0 ||
    //         user?.role?.some(
    //           (items) => items?.name === 'technician' && items?.category?.includes('Others')
    //         )
    //           ? ['technician']
    //           : []),
    //       ],
    //       children: [
    //         {
    //           title: 'Other Assets',
    //           path: paths.app.asset_management.other_assets,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //         },
    //         {
    //           title: 'Other Items',
    //           path: paths.app.asset_management.other_assets_items,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //         },
    //         {
    //           title: 'Alerts',
    //           path: paths.app.asset_management.other_assets_alerts,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //           info: (
    //             <Label color="warning">
    //               {user?.alertCounts?.Others > 99 ? ' 99+' : user?.alertCounts?.Others}
    //             </Label>
    //           ),
    //         },
    //         {
    //           title: 'QR List',
    //           path: paths.app.asset_management.other_assets_pdf,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //         },
    //       ],
    //     },
    //     ...(dynamicNav || []),
    //   ]?.filter(Boolean),
    // },
    // // AMC Contracts
    // isAmcRequired && {
    //   subheader: 'AMC CONTRACTS',
    //   userTypes: ['admin', 'assetadmin'],
    //   items: [
    //     {
    //       title: 'AMC Contracts',
    //       path: '/amc-contracts',
    //       icon: ICONS.user,
    //       userTypes: ['admin', 'assetadmin'],
    //     },
    //   ],
    // },
    // // Reports
    // {
    //   subheader: 'Reports',
    //   userTypes: ['admin', 'assetadmin'],
    //   items: [
    //     {
    //       title: ' Reports',
    //       path: paths.app.reports.root,
    //       icon: ICONS.analytics,
    //       userTypes: ['admin', 'assetadmin'],
    //     },
    //   ],
    // },
    // {
    //   subheader: 'ASSET SPEND SUMMARY',
    //   userTypes: ['admin', 'assetadmin'],
    //   items: [
    //     {
    //       title: 'Asset Spend Management',
    //       path: paths.app.assetSpendManagement.root,
    //       icon: <Iconify icon="solar:hand-money-bold-duotone" />,
    //       userTypes: ['admin', 'assetadmin'],
    //       children: [
    //         {
    //           title: ' Overview',
    //           path: paths.app.assetSpendManagement.assetSpendOverview,
    //           userTypes: ['admin', 'assetadmin'],
    //         },
    //         {
    //           title: 'User spends',
    //           path: paths.app.assetSpendManagement.userSpendList,
    //           userTypes: ['admin', 'assetadmin'],
    //         },
    //       ],
    //     },
    //     {
    //       title: 'Alerts',
    //       path: paths.app.alerts.root,
    //       icon: <Iconify icon="mingcute:windows-fill" />,
    //       userTypes: [
    //         'admin',
    //         ...(user?.role?.find((item) => item?.name === 'assetadmin')?.category?.length === 0 ||
    //         user?.role?.some(
    //           (item) => item?.name === 'assetadmin' && item?.category?.includes('Software')
    //         )
    //           ? ['assetadmin']
    //           : []),
    //         ...(user?.role?.find((item) => item?.name === 'technician')?.category?.length === 0 ||
    //         user?.role?.some(
    //           (items) => items?.name === 'technician' && items?.category?.includes('Software')
    //         )
    //           ? ['technician']
    //           : []),
    //       ],
    //       children: [
    //         isAmcRequired && {
    //           title: 'AMC Alerts',
    //           path: paths.app.alerts.root,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //         },
    //         {
    //           title: 'Warranty & Subscription Alerts',
    //           path: paths.app.alerts.warranty_subscription,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //         },
    //       ].filter(Boolean),
    //     },
    //   ],
    // },
    // // Ticket Management
    // {
    //   subheader: 'Ticket MANAGEMENT',
    //   userTypes: ['admin', 'assetadmin', 'technician', 'manager', 'itsupport'],
    //   items: [
    //     {
    //       title: 'Tickets',
    //       path: '/ticket',
    //       icon: ICONS.label,
    //       userTypes: ['admin', 'assetadmin', 'technician', 'manager', 'itsupport'],
    //       children: [
    //         {
    //           title: 'Approval Requests (Manager)',
    //           path: paths.app.tickets.manager_approvalList,
    //           userTypes: ['admin', 'manager'],
    //           info: <NewBadge code={user?.isNewManagerTicketCreated} />,
    //           onClick: () => {
    //             if (user?.isNewGeneralTicketCreated || user?.isNewManagerTicketCreated) {
    //               handleNewClick('manager'); // Call the API
    //             }
    //           },
    //         },
    //         {
    //           title: 'Active Tickets (Manager)',
    //           path: '/ticket/my-ticket/all-tickets',
    //           userTypes: ['manager'],
    //         },
    //         {
    //           title: 'Pending Tickets',
    //           path: paths.app.tickets.approval_list,
    //           userTypes: ['admin', 'assetadmin'],
    //           info: (
    //             <>
    //               <NewBadge code={user?.isNewTicketCreated} />
    //               <Label color="warning">{user?.allCount > 99 ? ' 99+' : user?.allCount}</Label>
    //             </>
    //           ),
    //           onClick: () => {
    //             if (user?.isNewTicketCreated) {
    //               handleNewClick('assetadmin'); // Call the API
    //             }
    //           },
    //           // info: ,
    //         },
    //         {
    //           title: 'Active Tickets',
    //           path: paths.app.tickets.ticket_list,
    //           userTypes: ['admin', 'assetadmin'],
    //           info: (
    //             <Label color="success">
    //               {user?.allStageCount > 99 ? ' 99+' : user?.allStageCount}
    //             </Label>
    //           ),
    //         },
    //         {
    //           title: 'General Tickets',
    //           path: paths.app.tickets.it_support_list,
    //           userTypes: ['itsupport'],
    //           info: <NewBadge code={user?.isNewGeneralTicketCreated} />,
    //           onClick: () => {
    //             // if (user?.isNewGeneralTicketCreated || user?.isNewManagerTicketCreated) {
    //             handleNewClick('itsupport'); // Call the API
    //             // }
    //           },
    //         },

    //         {
    //           title: 'Active Tickets (Technician)',
    //           path: paths.app.tickets.technician_approval_list,
    //           userTypes: ['technician'],
    //           info: (
    //             <>
    //               <NewBadge code={user?.isNewTicketCreated} />
    //               <Label color="success">
    //                 {user?.allStageCount > 99 ? ' 99+' : user?.allStageCount}
    //               </Label>{' '}
    //             </>
    //           ),
    //           onClick: () => {
    //             if (user?.isNewTicketCreated) {
    //               handleNewClick('technician'); // Call the API
    //             }
    //           },
    //         },

    //         {
    //           title: 'Closed Tickets',
    //           path: paths.app.tickets.closed_ticket_list,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //           info: (
    //             <Label color="error">{user?.closedCount > 99 ? ' 99+' : user?.closedCount}</Label>
    //           ),
    //         },
    //         {
    //           title: 'Modification Requests',
    //           path: paths.app.tickets.modification,
    //           userTypes: ['admin', 'assetadmin', 'technician'],
    //         },
    //       ],
    //     },
    //     {
    //       title: 'General Tickets',
    //       path: '/it-support',
    //       icon: ICONS.label,
    //       userTypes: ['admin'],
    //     },
    //     ...(user?.isOnTaskAdded
    //       ? [
    //           {
    //             title: 'External Tickets',
    //             path: '#',
    //             onClick: () => {
    //               handleCreateToken();
    //             },
    //             icon: ICONS.label,
    //             info: <Iconify width={18} icon="prime:external-link" />,
    //             userTypes: ['admin', 'itsupport'],
    //           },
    //         ]
    //       : []),
    //   ],
    // },

    // {
    //   subheader: 'Configurations',
    //   userTypes: ['admin', 'assetadmin'],
    //   items: [
    //     {
    //       title: 'Master Configurations',
    //       path: paths.app.settings.root,
    //       icon: ICONS.label,
    //       userTypes: ['admin', 'assetadmin'],
    //       children: [
    //         {
    //           title: 'Users',
    //           path: paths.app.settings.users,
    //           userTypes: ['admin', 'assetadmin'],
    //         },
    //         {
    //           title: 'Asset Configurations',
    //           path: paths.app.settings.asset_config,
    //           userTypes: ['admin', 'assetadmin'],
    //         },
    //         {
    //           title: 'Asset Checklist',
    //           path: paths.app.settings.asset_checklist,
    //           userTypes: ['admin', 'assetadmin'],
    //         },
    //         {
    //           title: 'Ticket Configurations',
    //           path: paths.app.settings.ticket,
    //           userTypes: ['admin', 'assetadmin'],
    //         },
    //         {
    //           title: 'Tag Configurations',
    //           path: paths.app.settings.tag_list,
    //           userTypes: ['admin', 'assetadmin'],
    //         },
    //         {
    //           title: 'Vendors & Resellers',
    //           path: '/settings/vendor-reseller',
    //           userTypes: ['admin', 'assetadmin'],
    //         },
    //         // {
    //         //   title: 'Vendors',
    //         //   path: paths.app.settings.vendor,
    //         //   userTypes: ['admin', 'assetadmin'],
    //         // },
    //         // {
    //         //   title: 'Resellers',
    //         //   path: paths.app.settings.company,
    //         //   userTypes: ['admin', 'assetadmin'],
    //         // },
    //         {
    //           title: 'Locations',
    //           path: paths.app.settings.location,
    //           userTypes: ['admin', 'assetadmin'],
    //         },
    //         {
    //           title: 'Currency Manager',
    //           path: paths.app.settings.currency_manager,
    //           userTypes: ['admin', 'assetadmin'],
    //         },
    //       ],
    //     },
    //   ],
    // },

    // {
    //   subheader: 'Subscription',
    //   userTypes: [],
    //   items: [
    //     {
    //       title: 'Subscription',
    //       path: paths.app.subscriptionAdmin.root,
    //       userTypes: [],
    //       icon: ICONS.subscription,
    //     },
    //   ],
    // },

    // {
    //   subheader: 'KNOWLEDGE BASE',
    //   userTypes: ['admin', 'assetadmin'],
    //   items: [
    //     {
    //       title: 'Knowledge Base',
    //       path: 'knowledge-base',
    //       userTypes: ['admin', 'assetadmin'],
    //       icon: ICONS.folder,
    //     },
    //   ],
    // },
  ].filter(Boolean);
};
